import React from 'react';
import { Clock, User, AlertTriangle, FileText, Activity, CheckCircle } from 'lucide-react';

export default function RecentHistoriesSection({ data, loading }) {
  // Helper function to get icon based on model type
  const getModelIcon = (modelName) => {
    switch (modelName) {
      case 'UserHistory':
        return <User className="h-4 w-4" />;
      case 'RiskHistory':
        return <AlertTriangle className="h-4 w-4" />;
      case 'ActivityHistory':
        return <Activity className="h-4 w-4" />;
      case 'ProcessHistory':
      case 'ProcessTaskHistory':
        return <FileText className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Helper function to get badge color based on model type
  const getModelColor = (modelName) => {
    switch (modelName) {
      case 'UserHistory':
        return 'bg-purple-100 text-green-600';
      case 'RiskHistory':
        return 'bg-red-100 text-red-800';
      case 'ActivityHistory':
        return 'bg-orange-100 text-orange-800';
      case 'ProcessHistory':
        return 'bg-blue-100 text-green-800';
      case 'ProcessTaskHistory':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get action type color
  const getActionTypeColor = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case 'create':
      case 'created':
        return 'text-green-600';
      case 'update':
      case 'updated':
        return 'text-blue-600';
      case 'delete':
      case 'deleted':
        return 'text-red-600';
      case 'approve':
      case 'approved':
        return 'text-green-600';
      case 'reject':
      case 'rejected':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Helper function to get model display name
  const getModelDisplayName = (modelName) => {
    switch (modelName) {
      case 'UserHistory':
        return 'Utilisateur';
      case 'RiskHistory':
        return 'Risque';
      case 'ActivityHistory':
        return 'Activité';
      case 'ProcessHistory':
        return 'Processus';
      case 'ProcessTaskHistory':
        return 'Tâche';
      default:
        return modelName;
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Activités récentes</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Activités récentes</h3>
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucune activité récente</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Activités récentes</h3>
        <span className="text-sm text-gray-500">{data.length} dernières actions</span>
      </div>

      <div className="space-y-3">
        {data.map((history, index) => (
          <div 
            key={index}
            className="flex items-start space-x-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
          >
            {/* Icon */}
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getModelColor(history.model)}`}>
              {getModelIcon(history.model)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${getModelColor(history.model)}`}>
                  {getModelDisplayName(history.model)}
                </span>
                {history.action_type && (
                  <span className={`text-xs font-medium ${getActionTypeColor(history.action_type)}`}>
                    {history.action_type}
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-900 mb-1">
                {history.description}
              </p>
              
              <div className="flex items-center space-x-3 text-xs text-gray-500">
                {history.done_by && (
                  <span className="flex items-center space-x-1">
                    <User className="h-3 w-3" />
                    <span>{history.done_by}</span>
                  </span>
                )}
                {history.target && (
                  <span className="truncate max-w-xs" title={history.target}>
                    → {history.target}
                  </span>
                )}
              </div>
            </div>

            {/* Time */}
            <div className="flex-shrink-0 text-xs text-gray-400">
              {formatDate(history.action_date)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}